// O que se vê quando uma página se recusa a abrir (IDEA-003 v2).
//
// Havia um caso concreto e ele aconteceu mesmo: a app instalada no telemóvel
// ficou com uma versão antiga guardada, e ao abrir o Jogo da Memória pediu um
// ficheiro que já não existe no servidor —
//
//   Failed to fetch dynamically imported module:
//   https://historia.nunoamorim.dev/assets/JogosMemoria-BGnbAo_f.js
//
// ...e o que apareceu no ecrã foi o erro em bruto do React Router, em inglês,
// com um "Unexpected Application Error!". Numa app que é nossa e que se abre na
// cama, isso é o pior fim possível.
//
// ─────────────────────────────────────────────────────────────
//  Porque é que isto acontece
// ─────────────────────────────────────────────────────────────
//  As páginas pesadas (mapa, calendário, os tabuleiros) são carregadas à parte,
//  cada uma no seu ficheiro com um nome que muda a cada build. A app instalada
//  guarda a versão que tinha; quando sai uma nova, os ficheiros antigos deixam
//  de existir no servidor. Enquanto o service worker não trocar de versão, a
//  app pede ficheiros que já ninguém tem.
//
//  A app só actualiza quando lhe dizem (`registerType: "prompt"` — não quereria
//  que se recarregasse sozinha a meio de uma carta). O preço disso é este: quem
//  for dizendo "agora não" acaba nesta situação. Portanto a recuperação tem de
//  estar aqui.
//
// ─────────────────────────────────────────────────────────────
//  O que faz
// ─────────────────────────────────────────────────────────────
//  Deita fora o service worker e as caches dele, e recarrega — uma vez só. O
//  `sessionStorage` é o que garante o "uma vez": sem ele, uma página que falhe
//  por outra razão qualquer entrava num ciclo de recargas, que é bastante pior
//  do que uma mensagem de erro.

import { useEffect, useState } from "react";
import { Link, useRouteError } from "react-router";

const CHAVE = "historia.recuperacao";

/** É um ficheiro que a app pediu e já não existe? */
function eModuloEmFalta(erro: unknown): boolean {
  const texto =
    erro instanceof Error ? `${erro.name} ${erro.message}` : String(erro ?? "");

  // As três formas conhecidas: Chrome/Android, Safari/iOS e Firefox dizem-no
  // cada um à sua maneira.
  return (
    /failed to fetch dynamically imported module/i.test(texto) ||
    /importing a module script failed/i.test(texto) ||
    /error loading dynamically imported module/i.test(texto)
  );
}

/** Larga a versão guardada e volta a pedir tudo ao servidor. */
async function largarVersaoGuardada(): Promise<void> {
  try {
    const registos = (await navigator.serviceWorker?.getRegistrations?.()) ?? [];
    await Promise.all(registos.map((r) => r.unregister()));
  } catch {
    // Sem service worker, ou o browser recusou. Segue para as caches.
  }

  try {
    const chaves = (await caches?.keys?.()) ?? [];
    await Promise.all(chaves.map((chave) => caches.delete(chave)));
  } catch {
    // Idem. O reload a seguir ainda pode resolver.
  }
}

export function ErroDaRota() {
  const erro = useRouteError();
  const [aRecuperar, setARecuperar] = useState(false);

  const desactualizada = eModuloEmFalta(erro);

  useEffect(() => {
    if (!desactualizada) return;

    // Uma tentativa por sessão. Se recarregar não chegou, não vale a pena
    // insistir — e um ciclo de recargas é pior do que dizer o que se passa.
    let jaTentou = false;
    try {
      jaTentou = sessionStorage.getItem(CHAVE) === "1";
      sessionStorage.setItem(CHAVE, "1");
    } catch {
      // Sem sessionStorage (Safari privado) não há como contar as tentativas:
      // mais vale não recarregar de todo do que arriscar o ciclo.
      jaTentou = true;
    }

    if (jaTentou) return;

    setARecuperar(true);
    void largarVersaoGuardada().then(() => window.location.reload());
  }, [desactualizada]);

  if (aRecuperar) {
    return (
      <main className="mx-auto flex min-h-[60dvh] max-w-md flex-col items-center justify-center px-6 text-center">
        <p className="text-3xl" aria-hidden>
          ✨
        </p>
        <p className="mt-3 text-tinta-700" role="status">
          A ir buscar a versão nova…
        </p>
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-[60dvh] max-w-md flex-col items-center justify-center px-6 text-center">
      <p className="text-4xl" aria-hidden>
        {desactualizada ? "🧹" : "💛"}
      </p>

      <h1 className="mt-4 font-display text-2xl text-tinta">
        {desactualizada ? "Esta página ficou para trás" : "Algo correu mal"}
      </h1>

      <p className="mt-2 text-sm leading-relaxed text-tinta-700">
        {desactualizada
          ? "A app tinha guardada uma versão antiga e esta página já não está lá. Limpar e voltar a abrir resolve."
          : "Não conseguimos abrir esta página. Tenta outra vez — e se insistir, volta ao início."}
      </p>

      <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
        <button
          type="button"
          onClick={() => {
            try {
              sessionStorage.removeItem(CHAVE);
            } catch {
              // Sem sessionStorage o botão funciona à mesma; só não se lembra.
            }
            void largarVersaoGuardada().then(() => window.location.reload());
          }}
          className="rounded-full bg-ouro px-6 py-2.5 text-sm font-semibold text-creme-50 transition-colors hover:bg-ouro-600"
        >
          Limpar e recarregar
        </button>

        <Link
          to="/"
          className="rounded-full border border-ouro/40 px-6 py-2.5 text-sm font-semibold text-tinta-700 transition-colors hover:bg-creme-200"
        >
          Ir para o início
        </Link>
      </div>
    </main>
  );
}
